import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format, addDays, subDays } from 'date-fns';
import { LOADING } from '@constants/requestPhase';
import {
  getVenueSections,
  createVenueSection,
  updateVenueSection,
  deleteVenueSection,
  getServicePeriods,
  createServicePeriod,
  updateServicePeriod,
  deleteServicePeriod,
  getGuests,
  createGuest,
  updateGuest,
  getReservations,
  createReservation,
  updateReservation,
  cancelReservation,
  getAvailability,
  getWaitlist,
  addToWaitlist,
  updateWaitlist,
} from '@store/reservations/duck';
import ReservationsView from '@components/views/Reservations';

const EMPTY_SECTION = {
  name: '',
  type: 'reservation',
  total_capacity: 20,
  reservation_capacity: '',
  description: '',
  is_active: true,
  sort_order: 0,
};

const EMPTY_SP = {
  name: '',
  days_of_week: [1, 2, 3, 4, 5], // Mon-Fri default
  start_time: '17:00',
  end_time: '22:00',
  slot_interval_mins: 30,
  max_covers: 40,
  turn_time_mins: 90,
  pacing_per_slot: '',
  is_active: true,
};

const Reservations = () => {
  const dispatch = useDispatch();
  const {
    venueSections,
    servicePeriods,
    guests,
    reservations,
    availability,
    waitlist,
    getReservationsPhase,
    createReservationPhase,
    getAvailabilityPhase,
  } = useSelector(state => state.reservations);

  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [activeTab, setActiveTab] = useState('reservations');

  // Booking modal
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    guest_id: '',
    party_size: 2,
    reservation_date: format(new Date(), 'yyyy-MM-dd'),
    reservation_time: '',
    service_period_id: '',
    notes: '',
    source: 'direct',
    status: 'confirmed',
  });

  // Guest modal
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [guestForm, setGuestForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    notes: '',
    vip: false,
  });

  // Waitlist modal
  const [waitlistModalOpen, setWaitlistModalOpen] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({
    guest_id: '',
    party_size: 2,
    waitlist_date: format(new Date(), 'yyyy-MM-dd'),
    preferred_time: '',
    quoted_wait_mins: 30,
  });

  // Venue Section modal
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState(EMPTY_SECTION);
  const [isSavingSection, setIsSavingSection] = useState(false);

  // Service Period modal
  const [spModalOpen, setSpModalOpen] = useState(false);
  const [editingSp, setEditingSp] = useState(null);
  const [spForm, setSpForm] = useState(EMPTY_SP);
  const [isSavingSp, setIsSavingSp] = useState(false);

  const [guestSearch, setGuestSearch] = useState('');

  useEffect(() => {
    dispatch(getVenueSections());
    dispatch(getServicePeriods());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getReservations(selectedDate));
    dispatch(getWaitlist(selectedDate));
  }, [dispatch, selectedDate]);

  useEffect(() => {
    if (activeTab === 'guests') dispatch(getGuests(guestSearch || undefined));
  }, [dispatch, activeTab, guestSearch]);

  const handleDateChange = useCallback((delta) => {
    const next = delta > 0
      ? format(addDays(new Date(selectedDate + 'T12:00:00'), delta), 'yyyy-MM-dd')
      : format(subDays(new Date(selectedDate + 'T12:00:00'), -delta), 'yyyy-MM-dd');
    setSelectedDate(next);
  }, [selectedDate]);

  const handleOpenBooking = useCallback((reservation = null, prefillDate = null, prefillTime = null) => {
    if (reservation) {
      setEditingReservation(reservation);
      setBookingForm({
        guest_id: reservation.guest_id,
        party_size: reservation.party_size,
        reservation_date: reservation.reservation_date,
        reservation_time: reservation.reservation_time,
        service_period_id: reservation.service_period_id || '',
        notes: reservation.notes || '',
        source: reservation.source,
        status: reservation.status,
      });
    } else {
      setEditingReservation(null);
      setBookingForm(prev => ({
        ...prev,
        reservation_date: prefillDate || selectedDate,
        reservation_time: prefillTime || '',
        guest_id: '',
        notes: '',
      }));
      if (prefillDate || selectedDate) {
        dispatch(getAvailability(prefillDate || selectedDate, 2));
      }
    }
    setBookingModalOpen(true);
  }, [selectedDate, dispatch]);

  const handlePartySizeChange = useCallback((party_size) => {
    setBookingForm(prev => ({ ...prev, party_size }));
    if (bookingForm.reservation_date) {
      dispatch(getAvailability(bookingForm.reservation_date, party_size));
    }
  }, [bookingForm.reservation_date, dispatch]);

  const handleBookingDateChange = useCallback((date) => {
    setBookingForm(prev => ({ ...prev, reservation_date: date }));
    dispatch(getAvailability(date, bookingForm.party_size));
  }, [bookingForm.party_size, dispatch]);

  const handleSaveBooking = useCallback(async () => {
    if (!bookingForm.guest_id || !bookingForm.reservation_time) return;
    if (editingReservation) {
      await dispatch(updateReservation({ id: editingReservation.id, ...bookingForm }));
    } else {
      await dispatch(createReservation(bookingForm));
    }
    setBookingModalOpen(false);
    dispatch(getReservations(selectedDate));
  }, [bookingForm, editingReservation, dispatch, selectedDate]);

  const handleCancelReservation = useCallback(async (id) => {
    await dispatch(cancelReservation({ id }));
    dispatch(getReservations(selectedDate));
  }, [dispatch, selectedDate]);

  const handleStatusChange = useCallback(async (id, status) => {
    await dispatch(updateReservation({ id, status }));
    dispatch(getReservations(selectedDate));
  }, [dispatch, selectedDate]);

  const handleSaveGuest = useCallback(async () => {
    await dispatch(createGuest(guestForm));
    setGuestModalOpen(false);
    setGuestForm({ first_name: '', last_name: '', email: '', phone: '', notes: '', vip: false });
    dispatch(getGuests());
  }, [guestForm, dispatch]);

  const handleSaveWaitlist = useCallback(async () => {
    if (!waitlistForm.guest_id) return;
    await dispatch(addToWaitlist(waitlistForm));
    setWaitlistModalOpen(false);
    setWaitlistForm({ guest_id: '', party_size: 2, waitlist_date: selectedDate, preferred_time: '', quoted_wait_mins: 30 });
    dispatch(getWaitlist(selectedDate));
  }, [waitlistForm, dispatch, selectedDate]);

  const handleWaitlistStatusChange = useCallback(async (id, status) => {
    await dispatch(updateWaitlist({ id, status }));
    dispatch(getWaitlist(selectedDate));
  }, [dispatch, selectedDate]);

  // Venue Section handlers
  const handleOpenSectionModal = useCallback((section = null) => {
    if (section) {
      setEditingSection(section);
      setSectionForm({
        name: section.name,
        type: section.type,
        total_capacity: section.total_capacity,
        reservation_capacity: section.reservation_capacity ?? '',
        description: section.description || '',
        is_active: section.is_active,
        sort_order: section.sort_order,
      });
    } else {
      setEditingSection(null);
      setSectionForm(EMPTY_SECTION);
    }
    setSectionModalOpen(true);
  }, []);

  const handleSaveSection = useCallback(async () => {
    setIsSavingSection(true);
    const payload = {
      ...sectionForm,
      reservation_capacity: sectionForm.reservation_capacity === '' ? null : Number(sectionForm.reservation_capacity),
    };
    if (editingSection) {
      await dispatch(updateVenueSection({ id: editingSection.id, ...payload }));
    } else {
      await dispatch(createVenueSection(payload));
    }
    setSectionModalOpen(false);
    setIsSavingSection(false);
    dispatch(getVenueSections());
  }, [sectionForm, editingSection, dispatch]);

  const handleDeleteSection = useCallback(async (id) => {
    if (!window.confirm('Delete this section?')) return;
    await dispatch(deleteVenueSection({ id }));
    dispatch(getVenueSections());
  }, [dispatch]);

  // Service Period handlers
  const handleOpenSpModal = useCallback((sp = null) => {
    if (sp) {
      setEditingSp(sp);
      setSpForm({
        name: sp.name,
        days_of_week: sp.days_of_week || [],
        start_time: sp.start_time,
        end_time: sp.end_time,
        slot_interval_mins: sp.slot_interval_mins,
        max_covers: sp.max_covers,
        turn_time_mins: sp.turn_time_mins,
        pacing_per_slot: sp.pacing_per_slot || '',
        is_active: sp.is_active,
      });
    } else {
      setEditingSp(null);
      setSpForm(EMPTY_SP);
    }
    setSpModalOpen(true);
  }, []);

  const handleSaveServicePeriod = useCallback(async () => {
    setIsSavingSp(true);
    const payload = {
      ...spForm,
      pacing_per_slot: spForm.pacing_per_slot === '' ? null : Number(spForm.pacing_per_slot),
    };
    if (editingSp) {
      await dispatch(updateServicePeriod({ id: editingSp.id, ...payload }));
    } else {
      await dispatch(createServicePeriod(payload));
    }
    setSpModalOpen(false);
    setIsSavingSp(false);
    dispatch(getServicePeriods());
  }, [spForm, editingSp, dispatch]);

  const handleDeleteServicePeriod = useCallback(async (id) => {
    if (!window.confirm('Delete this service period?')) return;
    await dispatch(deleteServicePeriod({ id }));
    dispatch(getServicePeriods());
  }, [dispatch]);

  const totalCovers = reservations.reduce((sum, r) => sum + r.party_size, 0);

  return (
    <ReservationsView
      selectedDate={selectedDate}
      activeTab={activeTab}
      venueSections={venueSections}
      servicePeriods={servicePeriods}
      guests={guests}
      reservations={reservations}
      availability={availability}
      waitlist={waitlist}
      totalCovers={totalCovers}
      isLoading={getReservationsPhase === LOADING}
      isAvailabilityLoading={getAvailabilityPhase === LOADING}
      isSaving={createReservationPhase === LOADING}

      bookingModalOpen={bookingModalOpen}
      editingReservation={editingReservation}
      bookingForm={bookingForm}
      guestModalOpen={guestModalOpen}
      guestForm={guestForm}
      waitlistModalOpen={waitlistModalOpen}
      waitlistForm={waitlistForm}
      guestSearch={guestSearch}

      sectionModalOpen={sectionModalOpen}
      editingSection={editingSection}
      sectionForm={sectionForm}
      isSavingSection={isSavingSection}

      spModalOpen={spModalOpen}
      editingSp={editingSp}
      spForm={spForm}
      isSavingSp={isSavingSp}

      onDateChange={handleDateChange}
      onTabChange={setActiveTab}
      onOpenBooking={handleOpenBooking}
      onCloseBooking={() => setBookingModalOpen(false)}
      onBookingFormChange={(field, value) => setBookingForm(prev => ({ ...prev, [field]: value }))}
      onPartySizeChange={handlePartySizeChange}
      onBookingDateChange={handleBookingDateChange}
      onSaveBooking={handleSaveBooking}
      onCancelReservation={handleCancelReservation}
      onStatusChange={handleStatusChange}

      onOpenGuestModal={() => setGuestModalOpen(true)}
      onCloseGuestModal={() => setGuestModalOpen(false)}
      onGuestFormChange={(field, value) => setGuestForm(prev => ({ ...prev, [field]: value }))}
      onSaveGuest={handleSaveGuest}
      onGuestSearch={setGuestSearch}

      onOpenWaitlistModal={() => {
        setWaitlistForm(prev => ({ ...prev, waitlist_date: selectedDate }));
        setWaitlistModalOpen(true);
      }}
      onCloseWaitlistModal={() => setWaitlistModalOpen(false)}
      onWaitlistFormChange={(field, value) => setWaitlistForm(prev => ({ ...prev, [field]: value }))}
      onSaveWaitlist={handleSaveWaitlist}
      onWaitlistStatusChange={handleWaitlistStatusChange}

      onOpenSectionModal={handleOpenSectionModal}
      onCloseSectionModal={() => setSectionModalOpen(false)}
      onSectionFormChange={(field, value) => setSectionForm(prev => ({ ...prev, [field]: value }))}
      onSaveSection={handleSaveSection}
      onDeleteSection={handleDeleteSection}

      onOpenSpModal={handleOpenSpModal}
      onCloseSpModal={() => setSpModalOpen(false)}
      onSpFormChange={(field, value) => setSpForm(prev => ({ ...prev, [field]: value }))}
      onSaveServicePeriod={handleSaveServicePeriod}
      onDeleteServicePeriod={handleDeleteServicePeriod}
    />
  );
};

export default Reservations;
